/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Quote, RestResponse } from '../model';
import { SearchableProvider } from "../model/interface/searchable.provider";
import { ApiHeaders } from './headers';
import { SystemType } from "../model/enums/system.type";
import { PresetValue } from "../model/preset-value";
import { Attachment } from "../model/quote";

@Injectable()
export class QuoteProvider implements SearchableProvider {
  currentPage:number=1;
  constructor(
    private http: HttpClient
  ) { }

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number, position?: string, type?: SystemType): Observable<RestResponse> {
    // in id we are passing selected tags
    return this.getQuotes(page ? page : 1, 25, true, 'CREATED_DATE', type, null, query);
  }

  getQuotes(page: number, size: number, sort: boolean, order: string, type: string, qType?: string, query?: string, state?: PresetValue[]): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/list?page=${page}&size=${size}&sort=${sort}&order=${order}`,
        {
          TYPE: type,
          QUERY_TYPE: qType,
          QUERY: query,
          QUOTE_STATE: state,
        },
        ApiHeaders.getOptions());
  }

  countQuotes(type: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/quote/report/count?type=${type}`, ApiHeaders.getOptions());
  }

  quoteLost(id: string, description: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/lost`, description, ApiHeaders.getOptions());
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  saveContact(quoteId: string, id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/quote/report/${quoteId}/contact/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  saveRevenue(quoteId: string, id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/quote/report/${quoteId}/revenue/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  saveOperates(quoteId: string, id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/quote/report/${quoteId}/operates/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  sendQuote(id: string, dataRequest: FormData): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/send`, dataRequest);
  }

  sendQuoteManually(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/send/manually`, null, ApiHeaders.getOptions());
  }

  followUps(id: string, dataRequest: FormData): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/followUp`, dataRequest);
  }

  submitQuote(contentRequest: FormData): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/submit`, contentRequest);
  }

  downloadFile(f: Attachment): Observable<any> {
    return this.http
      .post(`${environment.API_URL}/api/quote/report/download`, f, ApiHeaders.getOptionsJsonPlain());
  }

  sendNoQuote(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/no-quote`, null, ApiHeaders.getOptions());
  }

  revertQuote(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/revert`, null, ApiHeaders.getOptions());
  }

  getHistoryEmails(id: string, page: number,): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/history/emails?page=${page}`, ApiHeaders.getOptions());
  }

  additionalInfo(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/additional-info`, null, ApiHeaders.getOptions());
  }

  unlock(id: string, value: boolean): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/${id}/unlock`, value, ApiHeaders.getOptions());
  }

  getTotalBids(client_id: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/quote/report/client/${client_id}?systemType=${type}`, ApiHeaders.getOptions());
  }

  getLostOrders(client_id: string, type: SystemType): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/quote/report/client/${client_id}/lostOrders?systemType=${type}`, ApiHeaders.getOptions());
  }


  getBidComparison(client_id: string, type: SystemType ,fromDate: string, toDate: string, fromDateSecond: string, toDateSecond: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/quote/report/client/${client_id}/bidComparison?systemType=${type}`,
        {
          FROM_DATE: fromDate,
          TO_DATE: toDate,
          FROM_DATE_SECOND: fromDateSecond,
          TO_DATE_SECOND: toDateSecond,
        },
        ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'QUOTE';
  }
}
